import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Auth is via HttpOnly cookie set by backend (samesite=none, secure).
// We deliberately do NOT persist the token in localStorage/sessionStorage
// to avoid XSS-stealable credentials.
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  // Generous timeout so cold-starting preview pods (which can take 20-30s on
  // the first request of the day) don't surface as "Network Error".
  timeout: 45000,
});

// ---- Auto-retry on transient network failures ----
// The preview backend may be cold / restarting / briefly unreachable when a
// user returns the next day. We retry idempotent calls several times with
// exponential backoff + jitter before giving up.
const RETRYABLE_METHODS = new Set(["get", "head", "options"]);
const MAX_RETRIES = 5;
// Non-GET requests (e.g. password updates) only retry on *pure* network-level
// failures — the kind where the request never actually reached the backend
// (cold-starting server, DNS hiccup, dropped connection, client-side
// timeout). A smaller budget than GETs since the user is actively waiting
// on a form submit.
const POST_RETRY_METHODS_MAX_RETRIES = 3;

function isTransient(error) {
  if (!error) return false;
  if (error.code === "ERR_NETWORK") return true;
  if (error.code === "ECONNABORTED") return true; // axios timeout
  if (error.code === "ETIMEDOUT") return true;
  if (error.message === "Network Error") return true;
  const status = error.response?.status;
  // 502/503/504 = upstream cold start / restart / gateway. 408 = request timeout.
  if (status === 408 || status === 502 || status === 503 || status === 504) return true;
  return false;
}

// Subset of isTransient() that's safe to retry even for non-idempotent
// requests: no HTTP response was ever received, so we know the backend
// never processed the request (as opposed to 502/503/504, where a gateway
// *did* respond and the request may have partially executed downstream).
function isPureNetworkError(error) {
  if (!error) return false;
  if (error.response) return false; // got an HTTP response — not a pure network failure
  if (error.code === "ERR_NETWORK") return true;
  if (error.code === "ECONNABORTED") return true;
  if (error.code === "ETIMEDOUT") return true;
  if (error.message === "Network Error") return true;
  return false;
}

// Lightweight pub/sub so the AuthProvider can react to an expired session
// without each page having to handle 401s individually.
export const AUTH_EXPIRED_EVENT = "snooker:auth-expired";
let lastAuthEventAt = 0;
function emitAuthExpired() {
  const now = Date.now();
  // Debounce: avoid firing for every parallel request that failed.
  if (now - lastAuthEventAt < 1500) return;
  lastAuthEventAt = now;
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  } catch {
    // SSR / non-browser context — ignore.
  }
}

// Errors that should NOT bubble up as user-facing toasts.
// They are still rejected (so callers can short-circuit) but tagged so
// shared error helpers can skip showing them.
function markSilent(err, reason) {
  if (err && typeof err === "object") {
    err.isSilent = true;
    err.silentReason = reason;
  }
  return err;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error?.config;

    // 1) Auth expired / not authenticated → silently sign the user out.
    //    We never want a scary "Network error" or red toast for this case —
    //    the AuthProvider listens for AUTH_EXPIRED_EVENT and routes to /login.
    //    Endpoints that are "probes" or "auth surface area" are exempt so
    //    they can surface their own errors without flipping the global
    //    session-expired flag (which would show a stray toast on a fresh
    //    visit where the user simply has no cookie yet).
    const status = error?.response?.status;
    if (status === 401) {
      const url = (cfg?.url || "").toString();
      const isLoginCall = url.includes("/auth/login");
      const isMeProbe = url.includes("/auth/me");
      const isRegister = url.includes("/auth/register-cafe");
      const isReset = url.includes("/auth/reset-password") || url.includes("/auth/email-reset") || url.includes("/auth/phone-reset");
      const isAuthSurface = isLoginCall || isMeProbe || isRegister || isReset;
      if (!isAuthSurface) emitAuthExpired();
      return Promise.reject(markSilent(error, "unauthenticated"));
    }

    // 2) Subscription gate (402) — handled by SubscriptionGate component.
    if (status === 402) {
      return Promise.reject(markSilent(error, "subscription"));
    }

    // 3) Transient network/cold-start — retry a few times before surfacing.
    if (!cfg || !isTransient(error)) return Promise.reject(error);

    const method = (cfg.method || "get").toLowerCase();
    cfg.__retryCount = cfg.__retryCount || 0;

    const isGettable = RETRYABLE_METHODS.has(method);
    // Opted-in non-idempotent calls (e.g. password updates) only get to
    // retry when we're certain the backend never saw the request.
    const optedInPost = cfg.retryOnNetworkError === true && isPureNetworkError(error);
    const safe = isGettable || optedInPost;
    const maxRetries = isGettable ? MAX_RETRIES : POST_RETRY_METHODS_MAX_RETRIES;

    if (!safe || cfg.__retryCount >= maxRetries) {
      // Final failure: tag it silent so background polling doesn't spam toasts.
      // Callers that explicitly want to show the error can check isSilent themselves.
      return Promise.reject(markSilent(error, "transient"));
    }

    cfg.__retryCount += 1;
    // Exponential backoff with jitter: ~600ms, 1.2s, 2.4s, 4.8s, 9.6s
    const base = 600 * Math.pow(2, cfg.__retryCount - 1);
    const jitter = Math.floor(Math.random() * 300);
    await new Promise((r) => setTimeout(r, base + jitter));
    return api(cfg);
  }
);

export function isSilentError(err) {
  return !!(err && err.isSilent);
}

// Show an API error toast unless the error is one we deliberately handle
// silently (auth expiry, subscription gate, debounced cold-start retry).
// Drop-in replacement for `notifyApiError(toast, e)`.
//
// `toast` is the sonner toast import — pass it from the caller to avoid a
// circular import between the api layer and the UI layer.
export function notifyApiError(toast, err, fallback) {
  if (isSilentError(err)) return;
  const msg = formatApiError(err) || fallback || "Something went wrong";
  toast.error(msg);
}

export function formatApiError(err) {
  // Friendlier messages for transient connectivity issues so users never see
  // the raw "Network Error" string.
  if (isTransient(err)) {
    return "Reconnecting to the server… please try again in a moment.";
  }
  if (err?.response?.status === 401) {
    return "Your session has expired. Please sign in again.";
  }
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e?.msg ? e.msg : JSON.stringify(e)))
      .join(" ");
  return String(detail);
}

export const INR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
